// Serialize + save for the V2 assignment form (ADForm / AssignmentDashboardV2).
//
// The V2 form keeps a FLAT model (getBlankAssignmentForm in assignmentUtils.js)
// while /assignment expects the nested shape (projectId, assignmentDetails{...},
// schedule{...}, pricing{purchaseOrderId, workTypes[{customerPriceListId,...}]}).
// This module is the bridge in the form -> server direction; the inverse lives in
// parseAssignmentResponse.
//
// Deliberately separate from assignmentUtils.js: the legacy dashboard
// (AssignmentDashboard.vue + ADNewAssignmentModal) still saves through
// formatBackendData / processAssignmentsWithDatas, and nothing here touches that
// path. The payload shape below mirrors the one the spreadsheet upload posts
// (addAssignmentRowsToDB), which is the proven contract for /assignment.

// libraries
import moment from "moment";
// internal
import {
  processAssignment,
  getAssignments,
  getTasks,
  processTask,
} from "@/utils/newDbUtils";
import { publishMessage } from "@/utils/slackUtils";

// The blank form uses these sentinels instead of null, so "untouched" has to be
// detected rather than falsy-checked.
export const EMPTY_PROJECT_NAME = "Empty";
export const EMPTY_REQUESTER_NAME = "Empty";

// formatBackendData applies the same fallback, so a record created here matches
// one created from the legacy form.
export const DEFAULT_REQUEST_TIME = "10:00 AM";

// Same guards the legacy form applies before notifying Slack.
const PROJECTS_WITHOUT_EDIT_MODE_MESSAGE = [
  "Harman Product Marketing",
  "iTunes Film/TV Writeups",
  "iTunes - Apple Books",
];
const REQUEST_TYPES_WITHOUT_MESSAGE = [
  "Project Management",
  "Development",
  "Administrative",
];

export const isRealProject = (project) =>
  !!project?.id && project.name !== EMPTY_PROJECT_NAME;

// Assignees arrive either as a project staff object ({ user: { id } }) or, before
// syncAssigneesWithStaffList runs, as the flat user the /assignment payload
// carries ({ id }). Both have to resolve to the user id the API wants.
export const staffUserId = (person) => person?.user?.id ?? person?.id ?? null;

const requesterUserId = (requester) => requester?.user?.id ?? null;

// Departments come off the enum as { id, value }; a loaded assignment may carry
// either the object or the bare id.
const departmentId = (department) =>
  typeof department === "object" && department !== null
    ? department.id ?? null
    : department ?? null;

// -1 is the sentinel the API reads as "clear the purchase order". The form writes
// the picked PO to `purchaseOrder`; `pricing.purchaseOrder` is only ever set when
// an existing assignment is parsed, so both have to be consulted.
const purchaseOrderId = (form) => {
  const picked = form.purchaseOrder ?? form.pricing?.purchaseOrder ?? null;
  if (picked === -1) return -1;
  return picked?.id ?? null;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Dates land here as "YYYY-MM-DD" from the pickers, as a Date, or as the
// [year, month, day] array Spring serializes LocalDate into.
export const toApiDate = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const [year, month, day] = value;
    if (!year || !month || !day) return null;
    return moment({ year: +year, month: +month - 1, day: +day }).format(
      "YYYY-MM-DD"
    );
  }
  const parsed = moment(
    value,
    value instanceof Date
      ? undefined
      : ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", moment.ISO_8601]
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

// The API stores times as "10:00 AM". The V2 pickers write 24-hour "HH:mm:00",
// and a parsed assignment can hand back the [hour, minute] array — so normalise
// all three instead of posting whatever the form happens to hold.
export const toApiTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) {
    const [hour, minute] = value;
    if (hour === undefined || hour === null) return null;
    return moment({ hour: +hour, minute: +(minute ?? 0) }).format("hh:mm A");
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = moment(raw, ["hh:mm A", "h:mm A", "HH:mm:ss", "HH:mm", "H:mm"]);
  return parsed.isValid() ? parsed.format("hh:mm A") : null;
};

// Pay period is a month: the picker gives "YYYY-MM", a parsed assignment can give
// "YYYY-MM-DD" or [year, month].
export const toApiPayPeriod = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const [year, month] = value;
    if (!year || !month) return null;
    return `${year}-${String(month).padStart(2, "0")}`;
  }
  const parsed = moment(String(value), [
    "YYYY-MM",
    "YYYY-MM-DD",
    moment.ISO_8601,
  ]);
  return parsed.isValid() ? parsed.format("YYYY-MM") : null;
};

// Schedule values are stored as office wall clock, not as instants — the legacy
// form converts the client's time into this zone before saving (see
// convertDeliveryDue in ADNewAssignmentItem.vue).
export const OFFICE_TIME_ZONE = "America/New_York";

// "Now" as an office wall clock, anchored to UTC exactly the way
// parseFormDateTime anchors a stored date+time. Comparing a deadline against
// Date.now() instead would skew every countdown by the viewer's own UTC offset.
export const officeWallClockNow = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: OFFICE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const part = (type) => Number(parts.find((el) => el.type === type)?.value);
  // Some ICU builds report midnight as hour 24 under hour12: false.
  const hour = part("hour") % 24;
  return Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    hour,
    part("minute"),
    0,
    0
  );
};

// A form date + time as one instant, anchored to UTC.
//
// Both halves are wall-clock values — the record stores no offset — so they are
// read as UTC and must be rendered with timeZone: "UTC". A deadline then shows
// the hour that was actually picked instead of being shifted by wherever the
// viewer happens to be. Accepts every shape the form and the API produce:
// "YYYY-MM-DD" / [y,m,d] dates and "HH:mm:ss" / "hh:mm A" / [h,m] times.
// Returns null when there is no usable date.
export const parseFormDateTime = (date, time) => {
  const day = toApiDate(date);
  if (!day) return null;
  const [year, month, dayOfMonth] = day.split("-").map(Number);

  let hours = 0;
  let minutes = 0;
  const apiTime = toApiTime(time);
  if (apiTime) {
    const parsed = moment(apiTime, "hh:mm A");
    hours = parsed.hours();
    minutes = parsed.minutes();
  }

  return new Date(Date.UTC(year, month - 1, dayOfMonth, hours, minutes, 0, 0));
};

// Today in the office timezone — the same default the legacy blank form applies
// to requestDate. en-CA formats as YYYY-MM-DD, so the shifted date needs no
// reparsing (feeding a locale string back through moment without a format is what
// makes the legacy version log a deprecation warning).
const todayInOfficeTz = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

// A due date+time as one comparable instant. Missing times sort to midnight so a
// date-only comparison still orders correctly.
const dueMoment = (date, time) => {
  const day = toApiDate(date);
  if (!day) return null;
  const parsed = moment(
    `${day} ${toApiTime(time) || "12:00 AM"}`,
    "YYYY-MM-DD hh:mm A"
  );
  return parsed.isValid() ? parsed : null;
};

// Rows are held as { customerPriceList: {...}, quantity, orderPosition } (the
// shape ADLogWorkType writes); the API wants the price list id. Rows without a
// price list are dropped rather than posted as half-filled entries.
const buildWorkTypes = (workTypes, assignmentStatus) => {
  const rows = (workTypes || [])
    .filter((el) => el && (el.customerPriceList || el.customerPriceListId))
    .map((el, index) => ({
      customerPriceListId:
        el.customerPriceList?.id ?? el.customerPriceListId ?? null,
      quantity: toNumberOrNull(el.quantity),
      orderPosition: el.orderPosition ?? index + 1,
    }));
  if (!rows.length) return null;
  // "Pending Hours" records are created before the hours are known, and the API
  // rejects a null quantity on them (same coercion processAssignmentsWithDatas
  // applies).
  if (assignmentStatus === "Pending Hours" && rows[0].quantity === null) {
    rows[0].quantity = 0;
  }
  return rows;
};

// A new assignment on a DubEditor project has to carry its fragment set from the
// start: the DubEditor tab can only edit a record the server already holds, so
// without this the tab would show fields that can never be saved. The shape
// mirrors what the legacy form and the spreadsheet upload both post — one section
// per assigned role, each seeded with the project's fragment definitions and
// empty text.
//
// Create only. On an update the stored fragments hold the actual translated text
// and are owned by /dub-editor/assignment; re-seeding them here would wipe it.
const buildDubEditorAssignment = (form) => {
  const project = form.relatedProject;
  if (form.id || !project?.dubEditorProject) return undefined;

  const definitions = project.dubEditor?.fragments;
  if (!definitions?.length) return undefined;

  const seedFor = (person) => {
    const staffId = staffUserId(person);
    if (!staffId) return null;
    return {
      staffId,
      fragments: definitions.map((fragment) => ({
        ...fragment,
        text: "",
        fragmentId: fragment.id,
      })),
      complete: false,
    };
  };

  const sections = {
    primaryFragments: seedFor(form.primary),
    secondaryFragments: seedFor(form.secondary),
    qcFragments: seedFor(form.qc),
  };

  // Roles that are not assigned get no section at all, the same way the legacy
  // builder skips them.
  const dubEditorAssignment = {};
  Object.keys(sections).forEach((key) => {
    if (sections[key]) dubEditorAssignment[key] = sections[key];
  });

  return Object.keys(dubEditorAssignment).length ? dubEditorAssignment : undefined;
};

// Flat V2 form -> /assignment request body.
export const buildAssignmentPayload = (form) => {
  const project = form.relatedProject;
  const assignmentStatus = form.status || null;

  return {
    projectId: isRealProject(project) ? project.id : null,
    clientId: project?.client?.id ?? null,
    clientRequesterId: requesterUserId(form.clientRequester),
    projectManagerId:
      form.projectManager?.id ?? project?.projectManager?.id ?? null,
    primaryId: staffUserId(form.primary),
    secondaryId: staffUserId(form.secondary),
    qcId: staffUserId(form.qc),
    department: departmentId(form.department),
    task: {
      taskId: form.taskId || null,
      id: form.taskDbId ?? null,
    },
    // Omitted entirely (undefined drops out of the JSON body) unless this is a
    // create on a DubEditor project.
    dubEditorAssignment: buildDubEditorAssignment(form),
    assignmentDetails: {
      // External (client-side) assignment id — distinct from the Cue id.
      assignmentId: form.externalAssignmentId || null,
      assignmentDetail: form.assignmentDetail || null,
      assignmentTitle: form.assignmentTitle || null,
      assignmentStatus,
      billed: form.isBilled ?? null,
      characterCount: toNumberOrNull(form.characterCount),
      clientFeedback: form.clientFeedback ?? null,
      clientReviewed: form.clientReviewed ?? null,
      contentId: form.contentId || null,
      emailSubjectLine: form.emailSubjectLine || null,
      fileLocation: form.fileLocation || null,
      genre: form.genre || null,
      geo: form.geo || null,
      keyword: form.keyword || null,
      // The record holds a single link; the tab collects a list, so the first one
      // is the one that persists (see the note in ADForm.publishAssignment).
      link: form.links?.[0] || null,
      mediaType: form.mediaType || null,
      note: form.note || null,
      primaryAccepted: form.primaryAccepted ?? null,
      secondaryAccepted: form.secondaryAccepted ?? null,
      // Editable in the form's Priority picker, so it has to be sent — without
      // it the field reported a successful save and dropped the value. Batch edit
      // writes the same key.
      priority: form.priority || null,
      requestType: form.requestType || null,
      sourceGeo: form.sourceGeo || null,
      slackTs: form.slackTs || null,
      taskId: form.taskId || null,
      urgent: form.urgent ?? null,
      wordCount: toNumberOrNull(form.wordCount),
    },
    pricing: {
      purchaseOrderId: purchaseOrderId(form),
      workTypes: buildWorkTypes(form.pricing?.workTypes, assignmentStatus),
    },
    schedule: {
      deliveryDate: toApiDate(form.deliveryDate),
      deliveryTime: toApiTime(form.deliveryTime),
      payPeriod: toApiPayPeriod(form.payPeriod),
      requestDate: toApiDate(form.requestDate) || todayInOfficeTz(),
      requestTime: toApiTime(form.requestTime) || DEFAULT_REQUEST_TIME,
      primaryDueDate: toApiDate(form.schedule?.primaryDueDate),
      primaryDueTime: toApiTime(form.schedule?.primaryDueTime),
      secondaryDueDate: toApiDate(form.schedule?.secondaryDueDate),
      secondaryDueTime: toApiTime(form.schedule?.secondaryDueTime),
      qcDueDate: toApiDate(form.schedule?.qcDueDate),
      qcDueTime: toApiTime(form.schedule?.qcDueTime),
    },
  };
};

// The two projects whose assignments carry client-facing metadata. `isCue` in
// ADNewAssignmentItem is the inverse of this test, and every rule it guards
// (Email Subject Line, Media Type, Keyword, File Location) applies only here.
const CLIENT_METADATA_PROJECTS = ["iTunes FC", "iTunes Music"];

const needsClientMetadata = (project) =>
  CLIENT_METADATA_PROJECTS.includes(project?.name);

// A text field counts as filled only once it holds something other than
// whitespace; the form writes "" rather than null when a value is cleared.
const hasText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

// Everything the API needs before it will accept the record, plus the ordering
// rules the legacy form enforces. Mirrors the `rules` set on ADNewAssignmentItem
// (the legacy "New Assignment" modal), which is the reference for what a
// publishable assignment looks like.
//
// Returns `{ messages, fields }`: `messages` are display-ready and complete, so
// the caller can surface every problem at once instead of failing on a 400;
// `fields` is a lookup of the form keys that failed, which is what the form
// paints red. Both are empty when the assignment is valid.
export const validateAssignmentForm = (form) => {
  const messages = [];
  const missing = [];
  const fields = {};

  // Flags the field for the form's own highlight and folds it into the single
  // "Missing required field(s)" line, the way the legacy modal listed them.
  const requireField = (key, label, filled) => {
    if (filled) return;
    fields[key] = true;
    missing.push(label);
  };

  // A rule that has its own explanation rather than just "this is empty".
  const fail = (key, message) => {
    if (key) fields[key] = true;
    messages.push(message);
  };

  const project = form.relatedProject;
  const clientMetadata = needsClientMetadata(project);

  requireField("relatedProject", "Project", isRealProject(project));
  requireField(
    "clientRequester",
    "Client Requester",
    !!requesterUserId(form.clientRequester)
  );
  requireField("requestType", "Request Type", !!form.requestType);
  requireField(
    "assignmentTitle",
    "Assignment Title",
    hasText(form.assignmentTitle)
  );
  requireField(
    "assignmentDetail",
    "Assignment Detail",
    hasText(form.assignmentDetail)
  );
  requireField("geo", "Target GEO", !!form.geo);
  requireField("status", "Status", !!form.status);
  requireField("payPeriod", "Pay Period", !!toApiPayPeriod(form.payPeriod));

  // Request needs only the date — the legacy form left its time of day to the
  // DEFAULT_REQUEST_TIME fallback. Delivery needs both: "Delivery Date (ET)" and
  // "Delivery Due (ET)" were separate required fields there, and the V2 form
  // merges them into one control.
  requireField("requestDate", "Request Date", !!toApiDate(form.requestDate));
  requireField(
    "deliveryDate",
    "Delivery Date/Time",
    !!toApiDate(form.deliveryDate) && !!toApiTime(form.deliveryTime)
  );

  requireField("primary", "Primary", !!staffUserId(form.primary));
  requireField(
    "primaryDue",
    "Primary Due Date/Time",
    !!toApiDate(form.schedule?.primaryDueDate) &&
      !!toApiTime(form.schedule?.primaryDueTime)
  );

  // Client-facing metadata: required for iTunes FC / iTunes Music only.
  if (clientMetadata) {
    requireField(
      "emailSubjectLine",
      "Email Subject Line",
      hasText(form.emailSubjectLine)
    );
    requireField("mediaType", "Media Type", !!form.mediaType);
    requireField("keyword", "Keyword", !!form.keyword);
    requireField("fileLocation", "File Location", !!form.fileLocation);
  }

  // Work Type 1 is what the assignment bills on, so it has to carry a price
  // list reference; its quantity is only waived while hours are still coming in.
  const firstWorkType = form.pricing?.workTypes?.[0];
  requireField(
    "workTypes",
    "Work Type 1",
    !!(firstWorkType?.customerPriceList?.id ?? firstWorkType?.customerPriceListId)
  );
  if (firstWorkType && form.status !== "Pending Hours") {
    requireField(
      "workTypeQuantity",
      "Work Type 1 Quantity",
      toNumberOrNull(firstWorkType.quantity) !== null
    );
  }

  if (missing.length)
    messages.push(`Missing required field(s): ${missing.join(", ")}.`);

  // Quantities are counts of work done; a negative one is always a typo.
  (form.pricing?.workTypes || []).forEach((workType, index) => {
    const quantity = toNumberOrNull(workType?.quantity);
    if (quantity !== null && quantity < 0)
      fail(
        "workTypeQuantity",
        `Work Type ${index + 1} quantity cannot be negative.`
      );
  });

  // The form can waive the deadline chain explicitly.
  if (!form.overrideDueDates) {
    const primaryDue = dueMoment(
      form.schedule?.primaryDueDate,
      form.schedule?.primaryDueTime
    );
    const secondaryDue = dueMoment(
      form.schedule?.secondaryDueDate,
      form.schedule?.secondaryDueTime
    );
    const qcDue = dueMoment(
      form.schedule?.qcDueDate,
      form.schedule?.qcDueTime
    );

    if (primaryDue && secondaryDue && !secondaryDue.isAfter(primaryDue))
      fail("secondaryDue", "Secondary Due Date cannot be before Primary Due Date.");
    if (secondaryDue && qcDue && !qcDue.isAfter(secondaryDue))
      fail("qcDue", "QC Due Date cannot be before Secondary Due Date.");
    if (!secondaryDue && primaryDue && qcDue && !qcDue.isAfter(primaryDue))
      fail("qcDue", "QC Due Date cannot be before Primary Due Date.");

    // The legacy form's dateAvailabile / timeAvailabile rules: no internal
    // deadline may fall after what was promised to the client.
    const clientDue = dueMoment(form.deliveryDate, form.deliveryTime);
    if (clientDue) {
      [
        ["primaryDue", "Primary", primaryDue],
        ["secondaryDue", "Secondary", secondaryDue],
        ["qcDue", "QC", qcDue],
      ].forEach(([key, label, due]) => {
        if (due && due.isAfter(clientDue))
          fail(
            key,
            `${label} Due Date is after the Delivery Date for the client.`
          );
      });
    }
  }

  // Same rule the dashboard's bulk status change and ADStatusSelector apply:
  // this project's adaptations cannot be delivered without a billable quantity.
  if (
    form.relatedProject?.id === 115 &&
    form.requestType === "Adaptation" &&
    form.status === "Delivered" &&
    (!toNumberOrNull(form.wordCount) ||
      !toNumberOrNull(form.pricing?.workTypes?.[0]?.quantity))
  ) {
    if (!toNumberOrNull(form.wordCount)) fields.wordCount = true;
    if (!toNumberOrNull(form.pricing?.workTypes?.[0]?.quantity))
      fields.workTypeQuantity = true;
    messages.push(
      "Word Count and Work Type quantity are required before this assignment can be Delivered."
    );
  }

  return { messages, fields };
};

// POST a new assignment / PUT an existing one. Never throws: processAssignment
// resolves the error response, so the caller gets a flag plus the response to
// hand to the flash message.
export const saveAssignmentForm = async ({ form }) => {
  const isUpdate = !!form.id;
  const payload = buildAssignmentPayload(form);
  const response = await processAssignment({
    data: payload,
    id: isUpdate ? form.id : undefined,
  });

  if (!response || response.status > 204) {
    return { ok: false, isUpdate, payload, response, id: form.id ?? null };
  }

  return {
    ok: true,
    isUpdate,
    payload,
    response,
    saved: response.data ?? null,
    id: response.data?.id ?? form.id ?? null,
  };
};

// Notify Slack the same way the legacy form does, under the same guards. Never
// rethrows: a Slack outage must not make a saved assignment look unsaved.
export const notifyAssignmentSaved = async ({
  form,
  payload,
  assignmentId,
  isUpdate,
}) => {
  try {
    const project = form.relatedProject;
    const details = payload.assignmentDetails;

    if (REQUEST_TYPES_WITHOUT_MESSAGE.includes(details.requestType)) return;
    // These projects only announce brand-new records.
    if (PROJECTS_WITHOUT_EDIT_MODE_MESSAGE.includes(project?.name) && isUpdate)
      return;
    if (project?.dubEditorProject) return;
    // Updates to iTunes FC records are not announced.
    if (isUpdate && project?.name === "iTunes FC") return;

    const slackIdKey = `slackId${project?.name === "iTunes FC" ? "Fc" : "Staff"}`;
    const tag = (person) => {
      if (!person) return null;
      const slackId = person.adminDetails?.[slackIdKey];
      if (slackId) return `<@${slackId}>`;
      const first = person.user?.firstName ?? person.firstName;
      const last = person.user?.lastName ?? person.lastName;
      return first ? `${first} ${last ?? ""}`.trim() : null;
    };

    const geoEntry = { assignments: [details.assignmentDetail] };
    const writer = tag(form.primary);
    const editor = tag(form.secondary);
    const reviewer = tag(form.qc);
    if (writer) geoEntry.writers = [writer];
    if (editor) geoEntry.editors = [editor];
    if (reviewer) geoEntry.reviewers = [reviewer];

    await publishMessage({
      mode:
        project?.name === "iTunes FC" ? "fcNotification" : "nonFcNotification",
      item: {
        ...payload,
        project,
        primary: form.primary,
        secondary: form.secondary,
        qc: form.qc,
        assignment: details.assignmentDetail,
        geos: [{ [details.geo]: geoEntry }],
        id: assignmentId,
      },
      // Swiss-German adaptations get their own copy.
      modifier:
        !isUpdate &&
        details.geo === "de-CH" &&
        details.requestType === "Adaptation"
          ? "de-CH-adaptation"
          : undefined,
    });
  } catch (err) {
    console.error("Assignment Slack notification failed:", err);
  }
};

// Promote the parent task once every assignment under it reached `status`.
//
// Keyed off the cueTaskId the API returns on the saved record. The legacy form
// rebuilt this key by concatenating a moment object with the task id, which
// stringifies to a full date and never matches — so this reads the value the
// server already computed instead of recomputing it.
export const syncParentTaskStatus = async ({ cueTaskId, status }) => {
  try {
    if (!cueTaskId || !status) return;

    const assignmentsResp = await getAssignments({
      query: { search: cueTaskId },
    });
    const siblings = (assignmentsResp?.content || []).filter(
      (el) => el.cueTaskId === cueTaskId
    );
    if (
      !siblings.length ||
      !siblings.every(
        (el) => el.assignmentDetails?.assignmentStatus === status
      )
    )
      return;

    const tasksResp = await getTasks({ query: { search: cueTaskId } });
    const task = (tasksResp?.content || []).find(
      (el) => el.cueTaskId === cueTaskId
    );
    if (!task) return;

    await processTask({
      data: { taskStatus: status },
      id: task.id,
      patch: true,
    });
  } catch (err) {
    console.error("Parent task status sync failed:", err);
  }
};
